/**
 * Portfolio Display Formatters
 * Converts raw database values to human-readable display formats
 */

/**
 * Convert lamports to SOL display string
 * @param {number|string} lamports - Raw lamports value
 * @param {number} decimals - Number of decimal places (default: 4)
 * @returns {string} Formatted SOL amount
 */
export function formatLamportsToSOL(lamports, decimals = 4) {
  if (lamports === null || lamports === undefined) {
    return '0.0000';
  }

  const lamportsNum = typeof lamports === 'string' ? Number(lamports) : lamports;
  const sol = lamportsNum / 1_000_000_000;

  return sol.toFixed(decimals);
}

/**
 * Convert token atoms to display string with 6 decimals
 * @param {number|string} atoms - Raw token atoms value
 * @param {number} decimals - Token decimals (default: 6)
 * @returns {string} Formatted token amount
 */
export function formatTokenAtoms(atoms, decimals = 6) {
  if (atoms === null || atoms === undefined) {
    return '0.000000';
  }

  const atomsNum = typeof atoms === 'string' ? Number(atoms) : atoms;
  const tokens = atomsNum / Math.pow(10, decimals);

  return tokens.toFixed(decimals);
}

/**
 * Format average entry price (lamports per token)
 * @param {number|string} avgEntry - Average entry in lamports per token (scaled by 1e6)
 * @returns {string} Formatted price in SOL
 */
export function formatAvgEntry(avgEntry) {
  if (avgEntry === null || avgEntry === undefined || avgEntry === 0) {
    return '0.000000';
  }

  const avgEntryNum = typeof avgEntry === 'string' ? Number(avgEntry) : avgEntry;
  // avg_entry is stored as (lamports_per_token * 1e6) for precision
  // To get SOL per token: (avg_entry / 1e6) / 1e9 = avg_entry / 1e15
  const solPerToken = avgEntryNum / 1_000_000_000_000_000;

  return solPerToken.toFixed(9);
}

/**
 * Format timestamp to readable date string
 * @param {string} timestamp - ISO timestamp string
 * @returns {string} Formatted date
 */
export function formatTimestamp(timestamp) {
  if (!timestamp) {
    return 'N/A';
  }

  try {
    const date = new Date(timestamp);
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (err) {
    return 'Invalid date';
  }
}

/**
 * Shorten a public key for display
 * @param {string} publicKey - Full public key
 * @returns {string} Shortened public key
 */
export function shortenPublicKey(publicKey) {
  if (!publicKey || publicKey.length < 8) {
    return publicKey || 'N/A';
  }

  return `${publicKey.slice(0, 4)}...${publicKey.slice(-4)}`;
}
